import { encodeBase64 } from '../security/bytes.ts';
import type { IsoDateTime, MediaId, OptionId, QuestionId } from '../model/brands.ts';
import type {
  CanonicalExam,
  CanonicalQuestion,
  MediaRef,
  Option,
} from '../model/canonical.ts';
import type { ReportReason } from '../model/session.ts';

/**
 * A corrected copy of an exam, written by the person who found the mistake.
 *
 * This closes the loop that "report a problem" opens. Until now a reported question produced a
 * reason and a comment that went into the exported result and no further — a note to nobody. What
 * an author actually needs is the fixed file.
 *
 * Two things shape the output, and both are stated in the file itself rather than assumed:
 *
 *  - **`derivedFrom`** records which exam this came from, by id, content version and content hash.
 *    That is what lets a session taken against the original still be matched to the corrected copy.
 *  - **`revisions`** on each edited question record when, why, and — as JSON Pointers — exactly
 *    what changed. A correction nobody can audit is a rumour.
 *
 * The output is written from the **canonical model**, not from the bytes that were imported. That
 * has one visible consequence worth knowing: defaults the original left implicit come out written
 * down, so a diff against the original shows more than the corrections. The file is a faithful
 * equivalent, not a minimal patch — which is the right trade when the alternative is keeping a
 * second copy of every exam in memory for the whole session on the chance that one is exported.
 */

export interface QuestionPatch {
  readonly questionId: QuestionId;
  /** Why the change, from the same list the report dialog offers. */
  readonly reason: ReportReason | 'typo' | 'media-description';
  readonly note?: string;
  readonly explanationSummary?: string;
  readonly explanationDetail?: string;
  /** For `single-choice`: the option that is actually right. */
  readonly correctOptionId?: OptionId;
  /** For `multiple-choice`: the options that are actually right. */
  readonly correctOptionIds?: readonly OptionId[];
  /** New text for particular options, items or targets, by id. */
  readonly optionText?: Readonly<Record<string, string>>;
  /** New descriptions for particular images, by media id. */
  readonly mediaDescription?: Readonly<Record<string, string>>;
}

export interface ExamPatch {
  readonly questions: readonly QuestionPatch[];
  /** Who made the corrections. Optional, and absent unless somebody typed it. */
  readonly by?: string;
}

export interface ReviewedExamOptions {
  readonly at: IsoDateTime;
  readonly producer: string;
}

export const EXAM_SCHEMA_VERSION = '1.0.0';

/** Whether a patch actually asks for anything. An empty one must not produce a "corrected" file. */
export function isEmptyPatch(patch: ExamPatch): boolean {
  return patch.questions.every((question) => changedFieldsOf(question).length === 0);
}

/**
 * What a patch changed, as JSON Pointers relative to the question.
 *
 * Pointers rather than field names because that is what the schema asks for, and because
 * `/interaction/options/2/text` says which option in a way that `optionText` never could.
 */
export function changedFieldsOf(patch: QuestionPatch, question?: CanonicalQuestion): string[] {
  const fields: string[] = [];

  if (patch.explanationSummary !== undefined) {
    fields.push('/explanation/summary');
  }
  if (patch.explanationDetail !== undefined) {
    fields.push('/explanation/detail');
  }
  if (patch.correctOptionId !== undefined) {
    fields.push('/interaction/correctOptionId');
  }
  if (patch.correctOptionIds !== undefined) {
    fields.push('/interaction/correctOptionIds');
  }

  for (const id of Object.keys(patch.optionText ?? {})) {
    fields.push(pointerToOption(id, question));
  }
  for (const id of Object.keys(patch.mediaDescription ?? {})) {
    fields.push(pointerToMedia(id, question));
  }

  return fields;
}

/**
 * The next content version.
 *
 * A corrected answer key is a MINOR change: a session scored against the old file and one scored
 * against this one disagree, and the version has to say so. Everything else — a typo, a clearer
 * explanation, a description for an image — is a PATCH.
 */
export function nextContentVersion(current: string, patch: ExamPatch): string {
  const parts = /^(\d+)\.(\d+)\.(\d+)$/.exec(current);
  const changesAnswers = patch.questions.some(
    (question) => question.correctOptionId !== undefined || question.correctOptionIds !== undefined,
  );

  if (parts === null) {
    // Not a version this can reason about. Saying 1.0.0 would be a claim; refusing to guess and
    // leaving the lineage to `derivedFrom` is not.
    return current;
  }

  const [major, minor, revision] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  return changesAnswers
    ? `${String(major)}.${String(minor + 1)}.0`
    : `${String(major)}.${String(minor)}.${String(revision + 1)}`;
}

/**
 * Writes the corrected exam as a document in the published format.
 *
 * Every question is written, not just the corrected ones: the result has to be a complete, usable
 * exam file rather than a patch somebody has to apply by hand.
 */
export function toReviewedExamJson(
  exam: CanonicalExam,
  patch: ExamPatch,
  options: ReviewedExamOptions,
): Record<string, unknown> {
  const byId = new Map(patch.questions.map((question) => [String(question.questionId), question]));

  return {
    $schema: `https://freexamplayer.com/schema/exam/${EXAM_SCHEMA_VERSION}/exam.schema.json`,
    kind: 'exam',
    schemaVersion: EXAM_SCHEMA_VERSION,
    exam: {
      id: exam.meta.id,
      title: exam.meta.title,
      ...(exam.meta.description !== undefined && { description: exam.meta.description }),
      contentVersion: nextContentVersion(exam.meta.contentVersion, patch),
      language: exam.meta.language,
      ...(exam.meta.authors.length > 0 && {
        authors: exam.meta.authors.map((author) => ({
          name: author.name,
          ...(author.url !== undefined && { url: author.url }),
        })),
      }),
      ...(exam.meta.license !== undefined && { license: exam.meta.license }),
      ...(exam.meta.source !== undefined && { source: { ...exam.meta.source } }),
      // The lineage, which is the whole reason this file can be trusted to be a copy of something.
      derivedFrom: {
        examId: exam.meta.id,
        contentVersion: exam.meta.contentVersion,
        contentHash: exam.contentHash,
        derivedAt: options.at,
        producer: options.producer,
      },
      // Only when the author declared one: a taxonomy collected from the questions was never
      // written by anybody, and writing it back would put words in their mouth.
      ...(exam.taxonomy.declared && {
        taxonomy: {
          categories: exam.taxonomy.categories.map(node),
          topics: exam.taxonomy.topics.map(node),
        },
      }),
      defaults: {
        pointsPerQuestion: exam.meta.defaults.pointsPerQuestion,
        ...(exam.meta.defaults.timeLimitMinutes !== undefined && {
          timeLimitMinutes: exam.meta.defaults.timeLimitMinutes,
        }),
        ...(exam.meta.defaults.passingScorePercent !== undefined && {
          passingScorePercent: exam.meta.defaults.passingScorePercent,
        }),
      },
      ...(Object.keys(exam.meta.extensions).length > 0 && { extensions: exam.meta.extensions }),
    },
    questions: exam.questions.map((question) =>
      writeQuestion(question, byId.get(String(question.id)), patch.by, options.at),
    ),
  };
}

function node(entry: { id: string; label: string; description?: string }): Record<string, unknown> {
  return {
    id: entry.id,
    label: entry.label,
    ...(entry.description !== undefined && { description: entry.description }),
  };
}

function writeQuestion(
  question: CanonicalQuestion,
  patch: QuestionPatch | undefined,
  by: string | undefined,
  at: IsoDateTime,
): Record<string, unknown> {
  const changed = patch === undefined ? [] : changedFieldsOf(patch, question);
  const revisions = [
    ...question.revisions.map((revision) => ({
      at: revision.at,
      ...(revision.by !== undefined && { by: revision.by }),
      reason: revision.reason,
      ...(revision.changedFields.length > 0 && { changedFields: [...revision.changedFields] }),
      ...(revision.note !== undefined && { note: revision.note }),
    })),
    // Appended, never replacing: the history of a question is the point of keeping it.
    ...(patch !== undefined && changed.length > 0
      ? [
          {
            at,
            ...(by !== undefined && by !== '' && { by }),
            reason: patch.reason,
            changedFields: changed,
            ...(patch.note !== undefined && patch.note !== '' && { note: patch.note }),
          },
        ]
      : []),
  ];

  const explanation = writeExplanation(question, patch);

  return {
    id: question.id,
    type: question.type,
    ...(hasClassification(question) && {
      classification: {
        ...(question.classification.categoryId !== undefined && {
          categoryId: question.classification.categoryId,
        }),
        ...(question.classification.topicIds.length > 0 && {
          topicIds: [...question.classification.topicIds],
        }),
        ...(question.classification.tags.length > 0 && { tags: [...question.classification.tags] }),
        ...(question.classification.difficulty !== undefined && {
          difficulty: question.classification.difficulty,
        }),
        ...(question.classification.cognitiveLevel !== undefined && {
          cognitiveLevel: question.classification.cognitiveLevel,
        }),
      },
    }),
    content: {
      prompt: question.content.prompt,
      ...(question.content.instructions !== undefined && {
        instructions: question.content.instructions,
      }),
      language: question.content.language,
      ...(question.content.media.length > 0 && {
        media: question.content.media.map((entry) => writeMedia(entry, patch)),
      }),
    },
    interaction: writeInteraction(question, patch),
    scoring: { points: question.scoring.points, mode: question.scoring.mode },
    ...(explanation !== null && { explanation }),
    ...(question.references.length > 0 && {
      references: question.references.map((reference) => ({
        title: reference.title,
        ...(reference.url !== undefined && { url: reference.url }),
        ...(reference.publisher !== undefined && { publisher: reference.publisher }),
        ...(reference.locator !== undefined && { locator: reference.locator }),
        ...(reference.retrievedAt !== undefined && { retrievedAt: reference.retrievedAt }),
      })),
    }),
    ...(revisions.length > 0 && { revisions }),
    metadata: {
      status: question.metadata.status,
      ...(question.metadata.source !== undefined && { source: { ...question.metadata.source } }),
      ...(question.metadata.createdAt !== undefined && { createdAt: question.metadata.createdAt }),
      ...(question.metadata.updatedAt !== undefined && { updatedAt: question.metadata.updatedAt }),
    },
    // Preserved verbatim. The format promises never to render them and never to lose them.
    ...(Object.keys(question.extensions).length > 0 && { extensions: question.extensions }),
  };
}

function hasClassification(question: CanonicalQuestion): boolean {
  const { categoryId, topicIds, tags, difficulty, cognitiveLevel } = question.classification;
  return (
    categoryId !== undefined ||
    topicIds.length > 0 ||
    tags.length > 0 ||
    difficulty !== undefined ||
    cognitiveLevel !== undefined
  );
}

function writeExplanation(
  question: CanonicalQuestion,
  patch: QuestionPatch | undefined,
): Record<string, unknown> | null {
  const summary = patch?.explanationSummary ?? question.explanation?.summary;
  const detail = patch?.explanationDetail ?? question.explanation?.detail;
  const media = question.explanation?.media ?? [];

  if (summary === undefined && detail === undefined && media.length === 0) {
    return null;
  }

  return {
    ...(summary !== undefined && { summary }),
    ...(detail !== undefined && { detail }),
    ...(media.length > 0 && { media: media.map((entry) => writeMedia(entry, patch)) }),
  };
}

/**
 * An image, written back out.
 *
 * Embedded bytes are re-encoded rather than dropped: an exam whose diagram survived the round trip
 * is worth more than a smaller file. Anything that was not embedded comes back as `missing`, which
 * is what it actually was — the normalizer resolved `package` and `remote` to that on the way in,
 * and claiming otherwise here would be inventing a file that does not exist.
 */
function writeMedia(media: MediaRef, patch: QuestionPatch | undefined): Record<string, unknown> {
  const description = patch?.mediaDescription?.[media.id] ?? media.description;

  return {
    id: media.id,
    role: media.role,
    availability: media.availability,
    ...(media.alt !== undefined && { alt: media.alt }),
    ...(description !== undefined && { description }),
    ...(media.originalFilename !== undefined && { originalFilename: media.originalFilename }),
    ...(media.source !== undefined && { source: media.source }),
    ...(media.availability === 'embedded' &&
      media.mimeType !== undefined &&
      media.payload !== undefined && {
        mimeType: media.mimeType,
        data: encodeBase64(media.payload.bytes),
      }),
  };
}

function writeOption(option: Option, patch: QuestionPatch | undefined): Record<string, unknown> {
  return {
    id: option.id,
    text: patch?.optionText?.[option.id] ?? option.text,
    ...(option.feedback !== undefined && { feedback: option.feedback }),
    ...(option.fixedPosition && { fixedPosition: true }),
    ...(option.media.length > 0 && { media: option.media.map((entry) => writeMedia(entry, patch)) }),
  };
}

function writeInteraction(
  question: CanonicalQuestion,
  patch: QuestionPatch | undefined,
): Record<string, unknown> {
  switch (question.type) {
    case 'single-choice':
      return {
        options: question.interaction.options.map((option) => writeOption(option, patch)),
        correctOptionId: patch?.correctOptionId ?? question.interaction.correctOptionId,
        ...(question.interaction.shuffleOptions !== undefined && {
          shuffleOptions: question.interaction.shuffleOptions,
        }),
      };

    case 'multiple-choice':
      return {
        options: question.interaction.options.map((option) => writeOption(option, patch)),
        correctOptionIds: [...(patch?.correctOptionIds ?? question.interaction.correctOptionIds)],
        selection: { min: question.interaction.selection.min, max: question.interaction.selection.max },
        ...(question.interaction.shuffleOptions !== undefined && {
          shuffleOptions: question.interaction.shuffleOptions,
        }),
      };

    case 'matching':
      return {
        items: question.interaction.items.map((item) => writeOption(item, patch)),
        targets: question.interaction.targets.map((target) => writeOption(target, patch)),
        pairs: question.interaction.pairs.map((pair) => ({ ...pair })),
        ...(question.interaction.allowTargetReuse && { allowTargetReuse: true }),
        ...(question.interaction.shuffleTargets !== undefined && {
          shuffleTargets: question.interaction.shuffleTargets,
        }),
      };

    case 'ordering':
      return {
        items: question.interaction.items.map((item) => writeOption(item, patch)),
        correctOrder: [...question.interaction.correctOrder],
        ...(question.interaction.orderLabel !== undefined && {
          orderLabel: { ...question.interaction.orderLabel },
        }),
      };

    case 'text':
      return {
        evaluation: question.interaction.evaluation,
        inputMode: question.interaction.inputMode,
        maxLength: question.interaction.maxLength,
        ...(question.interaction.accepted.length > 0 && {
          accepted: [...question.interaction.accepted],
        }),
        normalization: { ...question.interaction.normalization },
        ...(question.interaction.sampleAnswer !== undefined && {
          sampleAnswer: question.interaction.sampleAnswer,
        }),
      };
  }
}

/** Where an option lives inside its question, for the revision record. */
function pointerToOption(id: string, question?: CanonicalQuestion): string {
  if (question === undefined) {
    return `/interaction/options/${id}`;
  }
  const lists: readonly [string, readonly Option[]][] =
    question.type === 'single-choice' || question.type === 'multiple-choice'
      ? [['options', question.interaction.options]]
      : question.type === 'matching'
        ? [
            ['items', question.interaction.items],
            ['targets', question.interaction.targets],
          ]
        : question.type === 'ordering'
          ? [['items', question.interaction.items]]
          : [];

  for (const [name, list] of lists) {
    const index = list.findIndex((option) => option.id === (id as OptionId));
    if (index >= 0) {
      return `/interaction/${name}/${String(index)}/text`;
    }
  }
  return `/interaction/options/${id}`;
}

function pointerToMedia(id: string, question?: CanonicalQuestion): string {
  const index = question?.content.media.findIndex((media) => media.id === (id as MediaId)) ?? -1;
  return index >= 0 ? `/content/media/${String(index)}/description` : `/content/media/${id}`;
}
